import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { 
        withCredentials: true,
        headers
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data.user);
    localStorage.setItem("token", response.data.access_token);
    return response.data;
  };

  const register = async (email, password, name, role = "guest") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    setUser(response.data.user);
    localStorage.setItem("token", response.data.access_token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    setUser(null);
    localStorage.removeItem("token");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, googleLogin, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (processed) return;
    setProcessed(true);

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");

    if (sessionId) {
      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then((response) => {
          setUser(response.data.user);
          toast.success("Welcome back!");
          navigate("/couple", { replace: true, state: { user: response.data.user } });
        })
        .catch((error) => {
          toast.error("Authentication failed");
          navigate("/login", { replace: true });
        });
    } else {
      navigate("/login", { replace: true });
    }
  }, [navigate, setUser, processed]);

  return (
    <div className="min-h-screen bg-wedding-black flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-wedding-ivory/60">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole && user.role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Lazy load pages
import LandingPage from "./pages/LandingPage";
import WeddingPage from "./pages/WeddingPage";
import TreeDisplayPage from "./pages/TreeDisplayPage";
import LoginPage from "./pages/LoginPage";
import CoupleAdminLayout from "./pages/couple/CoupleAdminLayout";
import CoupleOverview from "./pages/couple/CoupleOverview";
import CouplePageEditor from "./pages/couple/CouplePageEditor";
import CoupleTreeManager from "./pages/couple/CoupleTreeManager";
import CoupleArtifacts from "./pages/couple/CoupleArtifacts";
import CoupleAnalytics from "./pages/couple/CoupleAnalytics";
import CoupleFeatures from "./pages/couple/CoupleFeatures";
import CoupleSDEManager from "./pages/couple/CoupleSDEManager";
import CoupleStoryManager from "./pages/couple/CoupleStoryManager";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminWeddings from "./pages/admin/AdminWeddings";
import AdminArtifacts from "./pages/admin/AdminArtifacts";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminRafflePage from "./pages/admin/AdminRafflePage";
import TokenRedirect from "./pages/TokenRedirect";
import GratitudePage from "./pages/GratitudePage";
import TreeSubmitPage from "./pages/TreeSubmitPage";
import SDEViewerPage from "./pages/SDEViewerPage";
import StoryPlayerPage from "./pages/StoryPlayerPage";
import RaffleDisplayPage from "./pages/RaffleDisplayPage";
import CoupleRaffleController from "./pages/couple/CoupleRaffleController";
import CoupleTreeQR from "./pages/couple/CoupleTreeQR";

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/t/:token" element={<TokenRedirect />} />
      <Route path="/tree/submit/:weddingId" element={<TreeSubmitPage />} />
      <Route path="/:slug/tree" element={<TreeDisplayPage />} />
      <Route path="/:slug/gratitude" element={<GratitudePage />} />
      <Route path="/:slug/sde/:sdeType" element={<SDEViewerPage />} />
      <Route path="/:slug/story" element={<StoryPlayerPage />} />
      <Route path="/:slug/raffle" element={<RaffleDisplayPage />} />
      <Route path="/:slug" element={<WeddingPage />} />

      {/* Couple Admin Routes */}
      <Route path="/couple" element={<ProtectedRoute requiredRole="couple_admin"><CoupleAdminLayout /></ProtectedRoute>}>
        <Route index element={<CoupleOverview />} />
        <Route path="edit" element={<CouplePageEditor />} />
        <Route path="tree" element={<CoupleTreeManager />} />
        <Route path="artifacts" element={<CoupleArtifacts />} />
        <Route path="analytics" element={<CoupleAnalytics />} />
        <Route path="features" element={<CoupleFeatures />} />
        <Route path="sde" element={<CoupleSDEManager />} />
        <Route path="story" element={<CoupleStoryManager />} />
        <Route path="raffle" element={<CoupleRaffleController />} />
        <Route path="tree-qr" element={<CoupleTreeQR />} />
      </Route>

      {/* Super Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="weddings" element={<AdminWeddings />} />
        <Route path="raffle" element={<AdminRafflePage />} />
        <Route path="artifacts" element={<AdminArtifacts />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay"></div>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: "#0A0A0A",
                border: "1px solid rgba(212, 175, 55, 0.3)",
                color: "#EDEDED",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
